/**
 * The chrome over the map: the key, the control column, the right-click menu, the sheet tabs,
 * the alert HUD, the site banner and the strip board's counts.
 *
 * Everything here is read at a glance, over a moving picture, and almost none of it was written
 * in words. The legend named six states in capitals that the map chip beside it named in
 * sentence case with different words; the right-click menu offered "Live Video Feed" for the
 * thing the rest of the product calls a camera; the alert HUD said "alarm" eleven times for the
 * noun `terms.ts` settles as "alert", and shortened acknowledge to `ACK`, which is on nobody's
 * allowlist.
 *
 * ONE STATE, ONE PAIR OF WORDS. The legend's rows are not new strings where the map already has
 * one: `Flying`, `On the ground`, `Link weak` and `Link lost` come from `CHIP_STATE_LABEL`
 * (#523), so the key beside a marker and the chip on the marker can never disagree. That is the
 * whole reason the copy module exists.
 */

import { plural, pluralWord } from './terms';
import { CHIP_STATE_LABEL } from './status';

/**
 * The map key.
 *
 * `title` is the only place "Map key" reaches the screen now that the trigger is one icon - a
 * native tooltip, which no `text-transform` touches, so it renders exactly as written here. The
 * rows carry no uppercase class either, which is why their case genuinely changes here:
 * `docs/operational-standards.md`
 * reserves capitals for a critical condition, and a legend is the one surface on the COP where
 * nothing is a condition at all - it is a reference table read while nothing is wrong.
 */
export const MAP_LEGEND = {
  title: 'Map key',
  toggle: 'Toggle map key',
  groups: {
    drones: 'Drones',
    altitude: 'Altitude (m)',
    tracks: 'Tracks',
    overlays: 'Overlays',
  },
  rows: {
    flying: CHIP_STATE_LABEL.flying,
    grounded: CHIP_STATE_LABEL.grounded,
    warning: 'Warning',
    /** The window is the part an operator cannot guess, so it stays in the row. */
    stale: `${CHIP_STATE_LABEL.stale} (30 to 60 s)`,
    lost: `${CHIP_STATE_LABEL.lost} (over 60 s)`,
    selected: 'Selected',
    /** "Trail" is the code's word for it; the menu, the control and this row now all say track. */
    trail: 'Track (30 s)',
    controller: 'Controller',
    traffic: 'Other traffic',
    site: 'Site',
    dock: 'Dock',
    /** An external trigger nobody has answered yet - today, a person a dock's
     *  camera saw. Not "person": the row names the class of thing on the map,
     *  and a second trigger source would land on the same symbol. */
    detection: 'Detection',
    /** Airspace classes are single letters in a regulation, not an abbreviation of ours. */
    airspaceClasses: 'Class B, C, D and E',
    tfr: 'TFR',
    notam: 'NOTAM',
    scheduled: 'Planned route',
    activeRoute: 'Route being flown',
    transit: 'Transit, distance and time',
    /** The line between a relief and the aircraft it relieves; the number is the time to the swap. */
    handover: 'Handover, time to swap',
    /**
     * Red and ringed: a hold, a fault, a recall, a stale read, manual control or a relief that never
     * lifted. Not "failed" - an unresolved launch may be flying - and the Coverage block is what the
     * COP calls the place that names the cause.
     */
    handoverAttention: 'Handover needs attention, check Coverage',
  },
  /** The ring radii are rendered by the caller, which is the only place that knows them. */
  rangeRings: (radii: string) => `Range rings ${radii}`,
} as const;

/**
 * The control column down the right of the map, and the two selection-scoped toggles under it.
 *
 * Titles were in title case and named the layer rather than what turning it on shows. The
 * follow and track pair were already written in words (they were rewritten for the RC screen),
 * so they keep their sentences and lose only the British spelling that rule 9 retires.
 */
export const MAP_CONTROL = {
  resetNorth: 'Reset north',
  myLocation: 'My location',
  fullscreen: 'Fullscreen',
  follow: (callsign: string) => `Keep the map centered on ${callsign}`,
  stopFollowing: (callsign: string) => `Stop following ${callsign}`,
  showTrack: (callsign: string) => `Draw ${callsign}'s track`,
  hideTrack: (callsign: string) => `Hide ${callsign}'s track`,
  /**
   * The measurement tool. Named for what it gives the operator rather than for
   * the gesture ("Measure"), because the two words it produces - range and
   * bearing - are the ones they will say out loud about the reading.
   *
   * The off label says "stop", not "hide": pressing it again discards the
   * measurement rather than parking it somewhere to be brought back.
   */
  rangeBearing: 'Measure range and bearing',
  stopRangeBearing: 'Stop measuring',
  /**
   * The readout row in the map key's pill, beside the scale bar, while a line
   * has only its first point. A finished reading carries no words at all - just
   * `437 m · 047°` - since the units already say which number is which.
   */
  rangeBearingPending: 'Click the second point',
  /**
   * The same half-finished measurement, with the tool PUT DOWN.
   *
   * Stateful, not imperative. Since the measurement outlives the mode, "click
   * the second point" can be on screen when the next click does something else
   * entirely - clears the operator's selection, or with Fly here armed,
   * commands an aircraft. A readout may describe; only an armed tool may
   * instruct.
   */
  rangeBearingIncomplete: 'Measurement incomplete',
  clearMeasurement: 'Clear',
} as const;

/**
 * The Layers menu, which is four of the control column's keys (slice 6).
 *
 * The keys said what pressing them would DO, because a 44 px icon has no room to
 * say what it is: "Show airspace: classes, TFR, NOTAM". A checkbox in a menu
 * shows its own state, so each label is the thing itself, and the sentence the
 * airspace key had to carry is a hint on the row.
 *
 * The basemap is a pair of radio items rather than a "switch to the other one"
 * verb: there are exactly two, and naming both is what makes the current one
 * legible without pressing anything.
 */
export const MAP_LAYERS_MENU = {
  trigger: 'Map layers',
  /**
   * What the Layers key says when the picture is filtered.
   *
   * The choice persists between sessions, so an operator can sit down to a map
   * with something missing and no hint why. The key carries a count, and its
   * label says so in words - `aria-label` is the only channel a screen reader
   * or a forced-colors operator has, and a badge alone is colour and shape.
   *
   * NOT "N classes hidden". In this product "class" already means AIRSPACE
   * class, and it means that two rows further down this same menu
   * (`airspaceHint`), so "2 classes hidden" is fairly read as two airspace
   * classes by exactly the operator who is reading fast. Up to two are NAMED,
   * which is the more useful answer anyway; past that the count is shorter than
   * the list and the menu is one press away.
   */
  triggerFiltered: (hiddenLabels: readonly string[], total: number) =>
    hiddenLabels.length > 0 && hiddenLabels.length <= 2
      ? `Map layers, ${hiddenLabels.join(' and ')} hidden`
      : `Map layers, ${hiddenLabels.length} of ${total} hidden`,
  assetGroup: 'On the map',
  assetDrones: 'Drones',
  assetDronesHint: 'Own drones, their tracks and rings',
  assetDocks: 'Docks',
  assetDocksHint: 'Docks and their safe land zones',
  assetControllers: 'RC controllers',
  /** On the row itself: this is the one switch here that changes nothing today. */
  assetControllersTag: 'No source yet',
  /** On a locked row: the selected asset is why this class cannot be taken off the map. */
  assetHeldTag: 'Selected',
  /** Says what it will do rather than pretending there is something to hide today. */
  assetControllersHint: 'Ground controllers, once they report position',
  assetTraffic: 'Traffic',
  assetTrafficHint: 'Cooperative RemoteID and ADS-B traffic',
  assetSites: 'Sites',
  airspace: 'Airspace',
  airspaceHint: 'Classes, TFR and NOTAM',
  missionRoutes: 'Mission routes',
  altitudeLabels: 'Altitude on labels',
  basemapGroup: 'Basemap',
  basemapDark: 'Dark',
  basemapStreet: 'Street',
  basemapSatellite: 'Satellite',
  /** Required by Google's Map Tiles terms wherever their imagery is displayed. */
  imageryCredit: 'Imagery ©2026 Google',
  /**
   * Opens the zones sheet in the right column.
   *
   * Not a view toggle like the four above, which is why it sits under its own
   * divider. It is here because it has to be somewhere: slice 8 moved the panel
   * into the right column and took its key off the map, and the column's tab
   * strip only exists while some sheet is already open - so on an idle COP
   * there was no way at all to reach the only surface in the product that can
   * author a zone.
   */
  openZones: 'Zones',
  openZonesHint: 'Draw and edit zones in the right column',
} as const;

/**
 * The map's right-click menu.
 *
 * "Live Video Feed" was three words for the camera, and "Flight Path" was a fourth word for the
 * track the legend, the control column and the drone panel each called something else.
 */
export const MAP_CONTEXT_MENU = {
  camera: 'Show camera',
  center: 'Center on the drone',
  follow: 'Follow',
  stopFollowing: 'Stop following',
  showTrack: 'Show track',
  hideTrack: 'Hide track',
  showControllerLink: 'Show controller link',
  hideControllerLink: 'Hide controller link',
  /**
   * Range rings and transit lines, moved off the map's control column by
   * amendment B of the declutter plan.
   *
   * They were never a global view toggle: `rangeRingAnchor` draws them for the
   * SELECTED airborne aircraft and for nothing else, which is what the old
   * column tooltip had to spell out in fourteen words. On the aircraft's own
   * menu the subject is the aircraft under the cursor, so the label is three.
   */
  showRangeRings: 'Show range rings',
  hideRangeRings: 'Hide range rings',
  /** Why the item refuses on an aircraft that is not in the air (D13). */
  rangeRingsNeedAir: 'Range rings are drawn around a drone in the air',
  /** The right-click menu on a measurement line: its heading and its one item. */
  measurement: 'Measurement',
  deleteMeasurement: 'Delete',
} as const;

/** The four tabs over the right-hand sheet. The fallbacks show when nothing is selected. */
export const SHEET_TAB = {
  missions: 'Missions',
  droneFallback: 'Drone',
  dockFallback: 'Dock',
  droneDisabled: 'Select a drone on the map or in the strip first',
  dockDisabled: 'Select a dock on the map or in the roster first',
  /**
   * The fourth tab, which was a key in the map's control column until slice 8.
   *
   * Plural, matching the panel's own heading and the noun the map key, the
   * geofence records and the API all use.
   */
  zones: 'Zones',
} as const;

/**
 * The alert HUD.
 *
 * `terms.ts` settles the noun as "alert", against #505's own T8 table, which proposed alarm:
 * the product says alert 14 times to 4, it matches the `/alerts` route and the API's own noun,
 * and an alarm promises a sound this product does not always make. This surface was the largest
 * remaining holdout, and it also shortened the one action an operator takes here to `ACK`.
 *
 * The counts go through `plural`, because "1 ALARMS" was reachable from the chip, the
 * accessible name and the toast alike.
 */
export const ALERT_HUD = {
  /** The chip, inside a CSS `uppercase` class: sentence case here renders as it did. */
  count: (n: number) => plural(n, 'alert', 'alerts'),
  none: 'No active alerts',
  listTitle: 'Active alerts',
  listLabel: 'Alert list',
  closeList: 'Close the alert list',
  mute: 'Mute alerts',
  unmute: 'Unmute alerts',
  acknowledge: 'Acknowledge',
  /** What the button does to a row that is already acknowledged: it clears it from the list. */
  clear: 'Clear',
  /** #537's own wording, in US spelling (rule 9). */
  flyTo: 'Center the map on this drone',
  /** The feed itself, when the surface cannot say whether anything is wrong (D21). */
  feedUnavailable: 'Alert feed unavailable',
  feedUnavailableRetrying: 'Alert feed unavailable. Retrying.',
  loading: 'Loading alerts',
  /** The screen-reader summary, spoken on every arrival. */
  summary: ({ unacked, total, severity, title }: {
    unacked: number;
    total: number;
    severity: string;
    title: string;
  }) =>
    `${unacked} unacknowledged of ${plural(total, 'active alert', 'active alerts')}. Most severe: ${severity}, ${title}.`,
  chipLabel: ({ total, unacked }: { total: number; unacked: number }) =>
    `${plural(total, 'active alert', 'active alerts')}, ${unacked} unacknowledged`,
  /** A burst too large to toast one by one. */
  burstToast: (n: number) => `${n} new ${pluralWord(n, 'alert', 'alerts')}`,
  burstDescription: 'Open the alert list to acknowledge',
  openList: 'Open',
  /** One alert, toasted. The severity is a word, never the wire's own `a.severity`. */
  toastTitle: ({ severity, title }: { severity: string; title: string }) => `${severity}: ${title}`,
  toastDescription: ({ callsign, message }: { callsign: string | null; message: string }) =>
    callsign ? `${callsign}: ${message}` : message,
} as const;

/**
 * Severity as a word.
 *
 * The toast rendered `a.severity.toUpperCase()`, which is a wire value shouted at an operator -
 * rule 4 and rule 5 in one string. Critical keeps its capitals because it is the one severity
 * `docs/operational-standards.md` says may have them.
 */
export const SEVERITY_LABEL = {
  critical: 'CRITICAL',
  warning: 'Warning',
  info: 'Info',
} as const;

export type SeverityKey = keyof typeof SEVERITY_LABEL;

/** The severity words allowed to shout, for the guard test's case rule. */
export const CRITICAL_SEVERITIES: ReadonlySet<string> = new Set([SEVERITY_LABEL.critical]);

/** The banner shown when the picture is filtered to one site. */
export const SITE_FILTER = {
  /**
   * The site picker's own name, which stands in for the banner that used to say
   * the same thing in a row of its own above the map (slice 7).
   *
   * The banner was three items - the word "Site", the name, and a CLEAR link -
   * on a row that appeared and disappeared IN the map's flex column, so setting
   * a filter resized the MapLibre canvas. Everything it said is now in the
   * control that sets the thing.
   */
  filteredTo: (name: string) => `Showing ${name} only`,
  /** "Clear" said what it did to the filter; this says what the operator gets back. */
  clear: 'Show all',
} as const;

/**
 * The strip board's two counts.
 *
 * `TRK` is an abbreviation of ours and goes. `AIR` stays a word rather than becoming the
 * glossary's "In the air": the header is a two-count row at the top of a 360 px board, and
 * tripling its width to say the same thing pushes the counts an operator is scanning off the
 * strip. Both render through a CSS `uppercase` class, as they did.
 */
export const STRIP_BOARD = {
  air: 'Air',
  tracked: 'Tracked',
} as const;

/**
 * The drone panel's three segments.
 *
 * `A/C` is the retired abbreviation `terms.ts` names first: it appeared on the same screen as
 * "drone" and "airframe", which is the drift the vocabulary exists to end. `FEED` was our word
 * for the camera.
 *
 * The first segment stays **Live**. "Now" was proposed and rejected in review: "live" is
 * already the word an operator uses for a picture and a reading that are arriving this second,
 * it is what this segment has always been called, and nothing about it is engineer-English.
 * The rule is to replace the words an operator would have to ask about, not every word.
 */
export const WORKSPACE_SEGMENT = {
  live: 'Live',
  feed: 'Camera',
} as const;

/**
 * The dock roster's two controls, which are NOT the same control.
 *
 * `openBar` / `minimiseBar` work the whole bar: minimised it is a dock icon and
 * an arrow, and nothing else is drawn. That one is always present, because it
 * is the only way back from minimised.
 *
 * `expand` / `collapse` work the chip TRACK once the bar is open - one scrolling
 * line, or wrapped onto several. That one is drawn only when the chips do not
 * fit: a permanent toggle on a two-dock roster is one more thing on the map that
 * does nothing, and this bar's whole point is costing the stage as little as
 * possible.
 *
 * The four strings have to stay tellable apart by ear, since a screen reader
 * announces them with no icon and no position to disambiguate them. The pair
 * that actually co-occurs is the REDUCING one - "Minimize the dock bar" on the
 * key and the track's own collapse beside it - and the track's used to be "Show
 * fewer docks", which was both confusable with it and untrue: it shows the same
 * docks on one line with paging, and the control that genuinely shows fewer
 * (none) is the one next to it. So the track speaks about LAYOUT and the key
 * speaks about the bar.
 *
 * US spelling on every operator-facing value, matching `Center on the drone`
 * above and the US-first product, even where the identifiers around them keep
 * this codebase's British spelling (`colour-literal-gate`, `stageCentreShiftPx`).
 */
export const DOCK_ROSTER = {
  label: 'Docks',
  openBar: 'Open the dock bar',
  minimiseBar: 'Minimize the dock bar',
  expand: 'Wrap the dock list onto several lines',
  collapse: 'Show the dock list on one line',
};
